import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient, signOut, useSession } from "../lib/auth.ts";

type SessionRow = {
  id: string;
  token: string;
  userId: string;
  expiresAt: string | Date;
  createdAt: string | Date;
  ipAddress?: string | null;
  userAgent?: string | null;
};

function formatUA(ua?: string | null): string {
  if (!ua) return "Unknown";
  // Cheap UA-string heuristic. We don't need a parser library for this —
  // showing the raw UA on hover is fine, and most modern browsers ship a
  // recognisable substring early in the string.
  if (/Edg\//.test(ua))    return "Edge";
  if (/Firefox\//.test(ua))return "Firefox";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return ua.slice(0, 32);
}

export function AccountPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: session, refetch: refetchSession } = useSession();
  const currentToken = session?.session?.token;

  const sessions = useQuery({
    queryKey: ["account", "sessions"],
    queryFn: async (): Promise<SessionRow[]> => {
      const r = await authClient.listSessions();
      return (r.data ?? []) as SessionRow[];
    },
  });

  // Whether this user already has a password credential. OAuth-only users
  // see a "Set password" form; users with a password see "Change password".
  const accounts = useQuery({
    queryKey: ["account", "providers"],
    queryFn: async () => {
      const r = await authClient.listAccounts();
      return (r.data ?? []) as Array<{ providerId: string }>;
    },
  });
  const hasPassword = accounts.data?.some((a) => a.providerId === "credential") ?? false;

  // ---- Profile (name) ----
  const [name, setName] = useState(session?.user?.name ?? "");
  // Sync local state once the session loads — keeps the form prefilled
  // without overriding mid-typing.
  useEffect(() => {
    if (session?.user?.name && name === "") setName(session.user.name);
  }, [session, name]);
  const [profileState, setProfileState] = useState<{ busy: boolean; err?: string; ok?: string }>({ busy: false });

  // ---- Email change ----
  const [emailDraft, setEmailDraft] = useState("");
  const [emailState, setEmailState] = useState<{ busy: boolean; err?: string; ok?: string }>({ busy: false });

  // ---- Password change ----
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwState, setPwState] = useState<{ busy: boolean; err?: string; ok?: string }>({ busy: false });
  const [revokeAllBusy, setRevokeAllBusy] = useState(false);

  // ---- Delete account ----
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteState, setDeleteState] = useState<{ busy: boolean; err?: string }>({ busy: false });

  return (
    <>
      <h1>Account</h1>

      <div className="card">
        <h3>Profile</h3>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setProfileState({ busy: true });
            const r = await authClient.updateUser({ name });
            if (r.error) {
              setProfileState({ busy: false, err: r.error.message ?? "Couldn't update profile." });
              return;
            }
            await refetchSession({ query: { disableCookieCache: true } });
            setProfileState({ busy: false, ok: "Saved." });
          }}
        >
          <label>
            Name
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="kv-grid">
            <span className="muted">Email</span>
            <span>
              {session?.user?.email}
              {session?.user?.emailVerified
                ? <span className="muted"> (verified)</span>
                : <span className="muted"> (unverified)</span>}
            </span>
          </div>
          {profileState.err && <div className="err">{profileState.err}</div>}
          {profileState.ok && <div className="muted">{profileState.ok}</div>}
          <button type="submit" disabled={profileState.busy}>
            {profileState.busy ? "Saving…" : "Save profile"}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>Change email</h3>
        <p className="muted">
          We'll send a verification link to your new address. Until you click
          it, you'll keep signing in with your current address.
        </p>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setEmailState({ busy: true });
            const r = await authClient.changeEmail({
              newEmail: emailDraft,
              callbackURL: window.location.origin + "/account",
            });
            if (r.error) {
              setEmailState({ busy: false, err: r.error.message ?? "Couldn't request email change." });
              return;
            }
            setEmailDraft("");
            setEmailState({ busy: false, ok: "Verification email sent (or change applied immediately if your current address is unverified)." });
          }}
        >
          <label>
            New email
            <input
              type="email"
              required
              autoComplete="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
            />
          </label>
          {emailState.err && <div className="err">{emailState.err}</div>}
          {emailState.ok && <div className="muted">{emailState.ok}</div>}
          <button type="submit" disabled={emailState.busy || !emailDraft}>
            {emailState.busy ? "Sending…" : "Request email change"}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>{hasPassword ? "Change password" : "Set password"}</h3>
        {!hasPassword && (
          <p className="muted">
            You signed in with a social provider, so there's no password on
            this account yet. Set one to also sign in with email + password
            — both methods will work afterwards.
          </p>
        )}
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setPwState({ busy: true });
            if (pw.next !== pw.confirm) {
              setPwState({ busy: false, err: "New password and confirmation don't match." });
              return;
            }
            let err: string | null = null;
            if (hasPassword) {
              const r = await authClient.changePassword({
                currentPassword: pw.current,
                newPassword: pw.next,
                // Don't auto-revoke — user opts in with the section below.
                revokeOtherSessions: false,
              });
              if (r.error) err = r.error.message ?? "Couldn't update password.";
            } else {
              // The React client's typings don't surface /set-password yet, so
              // call the endpoint directly. Same shape as Better-Auth's other
              // mutations: 200 = ok, !ok = body.message holds the reason.
              const r = await fetch("/v1/auth/set-password", {
                method:      "POST",
                headers:     { "Content-Type": "application/json" },
                credentials: "include",
                body:        JSON.stringify({ newPassword: pw.next }),
              });
              if (!r.ok) {
                const body = await r.json().catch(() => null) as { message?: string } | null;
                err = body?.message ?? `Couldn't set password (HTTP ${r.status}).`;
              }
            }
            if (err) {
              setPwState({ busy: false, err });
              return;
            }
            setPw({ current: "", next: "", confirm: "" });
            setPwState({ busy: false, ok: hasPassword ? "Password updated." : "Password set. You can now also sign in with email + password." });
            qc.invalidateQueries({ queryKey: ["account", "providers"] });
          }}
        >
          {hasPassword && (
            <label>
              Current password
              <input
                type="password"
                required
                autoComplete="current-password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
              />
            </label>
          )}
          <label>
            New password (8+ chars)
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            />
          </label>
          {pwState.err && <div className="err">{pwState.err}</div>}
          {pwState.ok && <div className="muted">{pwState.ok}</div>}
          <button type="submit" disabled={pwState.busy || accounts.isLoading}>
            {pwState.busy ? (hasPassword ? "Updating…" : "Setting…") : (hasPassword ? "Change password" : "Set password")}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>Active sessions</h3>
        {sessions.isLoading && <p className="muted">Loading…</p>}
        {sessions.error && <p className="err">Couldn't load sessions — {(sessions.error as Error).message}</p>}
        {sessions.data && sessions.data.length === 0 && (
          <p className="muted">No active sessions on file.</p>
        )}
        {sessions.data && sessions.data.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Browser</th>
                  <th>IP</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s) => {
                  const isCurrent = s.token === currentToken;
                  return (
                    <tr key={s.id} style={{ cursor: "default" }}>
                      <td title={s.userAgent ?? ""}>
                        {formatUA(s.userAgent)}
                        {isCurrent && <span className="muted"> (this browser)</span>}
                      </td>
                      <td><code className="code-sm">{s.ipAddress ?? "—"}</code></td>
                      <td>{new Date(s.createdAt).toLocaleString()}</td>
                      <td>{new Date(s.expiresAt).toLocaleString()}</td>
                      <td>
                        {!isCurrent && (
                          <button
                            className="secondary"
                            onClick={async () => {
                              const r = await authClient.revokeSession({ token: s.token });
                              if (r.error) {
                                alert(`Failed: ${r.error.message ?? "unknown"}`);
                                return;
                              }
                              qc.invalidateQueries({ queryKey: ["account", "sessions"] });
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {sessions.data && sessions.data.length > 1 && (
          <p className="mt">
            <button
              className="danger"
              disabled={revokeAllBusy}
              onClick={async () => {
                setRevokeAllBusy(true);
                const r = await authClient.revokeOtherSessions();
                setRevokeAllBusy(false);
                if (r.error) {
                  alert(`Failed: ${r.error.message ?? "unknown"}`);
                  return;
                }
                qc.invalidateQueries({ queryKey: ["account", "sessions"] });
              }}
            >
              {revokeAllBusy ? "Signing out…" : "Sign out everywhere else"}
            </button>
          </p>
        )}
      </div>

      <div className="card">
        <h3>Delete account</h3>
        <p className="muted">
          Permanently removes your user record. Tests synced from devices
          you own stay on the org until the org owner deletes them. If
          you're the only owner of an organisation, transfer ownership
          first or that organisation will be orphaned.
        </p>
        {!deleteOpen && (
          <button className="danger" onClick={() => setDeleteOpen(true)}>
            Delete my account…
          </button>
        )}
        {deleteOpen && (
          <form
            className="formstack mt"
            onSubmit={async (e) => {
              e.preventDefault();
              if (deleteConfirm !== "DELETE") {
                setDeleteState({ busy: false, err: 'Type DELETE (uppercase) to confirm.' });
                return;
              }
              setDeleteState({ busy: true });
              const r = await authClient.deleteUser({
                password: deletePw || undefined,
              });
              if (r.error) {
                setDeleteState({ busy: false, err: r.error.message ?? "Couldn't delete account." });
                return;
              }
              // Server returns either status:true (deleted now) or sends a
              // verification email. In dev we don't gate on verification, so
              // we sign out and bounce to /login.
              await signOut();
              nav("/login");
            }}
          >
            <label>
              Current password (required if your session is older than 5 minutes)
              <input
                type="password"
                autoComplete="current-password"
                value={deletePw}
                onChange={(e) => setDeletePw(e.target.value)}
              />
            </label>
            <label>
              Type <code>DELETE</code> to confirm
              <input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
            </label>
            {deleteState.err && <div className="err">{deleteState.err}</div>}
            <div className="row">
              <button
                type="submit"
                className="danger"
                disabled={deleteState.busy || deleteConfirm !== "DELETE"}
              >
                {deleteState.busy ? "Deleting…" : "Delete my account"}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeletePw("");
                  setDeleteConfirm("");
                  setDeleteState({ busy: false });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
